import React from "react";
import {Localizer} from "@/main/react/Types";

/**
 * The one container every new profile block renders inside: collapsed on a phone, always open in the
 * desktop rail.
 *
 * <p><b>Why this exists as a shared component rather than a pattern.</b> Decision D4 of the
 * profile-as-a-destination plan. The mobile identity card is already <b>726px of a 915px viewport</b>,
 * and this slice adds three more blocks to the same column. Three feature agents each inventing their
 * own collapse behaviour is three behaviours where the design says one, and the failure is not cosmetic
 * — it is the profile becoming a scroll on the viewport that carries most of the traffic.
 *
 * <h3>The breakpoint half is CSS, not state, and that is deliberate</h3>
 *
 * The obvious implementation reads the viewport — `matchMedia`, a resize listener, a
 * `useSyncExternalStore`. All three are wrong here and one of them is dangerous:
 *
 * <ul>
 *   <li>A store subscription puts this component under AGENTS.md rule 31, where a snapshot that changes
 *       identity for a write that changes nothing has crashed the production header three times. A
 *       viewport listener fires on every scroll-induced toolbar resize on mobile Safari, which is
 *       exactly the "write that changes nothing" shape.</li>
 *   <li>Anything read at mount disagrees with the server for one frame.</li>
 * </ul>
 *
 * So the desktop half is pure CSS — `lg:block` on the content, `lg:hidden` on the toggle — and React
 * state carries only the mobile open/closed flip. Below `lg` the state is the whole answer; at `lg` and
 * above the CSS wins regardless of what the state says, and the toggle is not rendered at all. There is
 * no snapshot to get wrong, nothing to mismatch at hydration, and no listener.
 *
 * <p>The consequence to know: `open` can be `false` while the content is visible (desktop). That is
 * correct and is why `aria-expanded` lives on a button that `lg:hidden` removes from the accessibility
 * tree at the same breakpoint — the two are never both live.
 *
 * <h3>Empty means absent</h3>
 *
 * A section with no content renders <b>nothing</b> — not a heading, not an empty card. An empty shell on
 * someone's profile reads as something broken, and on a stranger's profile it reads as an accusation.
 * The owner-facing invitation is a real child passed by the caller, not an empty state this component
 * invents; §4 of the design language already bans displaying a negative, and the group page shipped
 * three fabricated placeholder rules that visitors read as the owner's actual rules.
 *
 * Design: `designs/01-now/2026-08-11-profile-destination-slice-design.md` §7.
 */
export function ProfileCollapsibleSection(props: {
    title: string;
    localizer: Localizer;
    children?: React.ReactNode;
    /**
     * Opt out of the mobile collapse for a block whose whole value is being seen without a tap.
     * Still honoured at every breakpoint; it only changes the initial state below `lg`.
     */
    defaultOpen?: boolean;
    /** Stable browser-test hook. The toggle and the content derive theirs from it. */
    dataPlaywrightId?: string;
    className?: string;
}) {
    const {title, localizer, children, defaultOpen = false, dataPlaywrightId, className} = props;
    const [open, setOpen] = React.useState(defaultOpen);
    // useId, not a counter or the title: two sections could share a title across the rail and the main
    // column, and aria-controls pointing at the wrong region is worse than pointing at nothing.
    const contentId = React.useId();

    // Nothing to show is nothing to render — see the class docs. `false` is included because
    // `{condition && <Block/>}` is how every caller will gate its own content.
    if (children === null || children === undefined || children === false) {
        return null;
    }

    return (
        <section
            className={`w-full overflow-hidden rounded-xl border border-border bg-card p-4 ${className ?? ""}`}
            data-playwright-id={dataPlaywrightId}
        >
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold tracking-tight text-foreground">{title}</h2>

                {/* `lg:hidden` is load-bearing in both directions: it hides the control where the CSS
                    already forces the content open, and it takes the stale `aria-expanded` out of the
                    accessibility tree at the same breakpoint. */}
                <button
                    type="button"
                    onClick={() => setOpen((previous) => !previous)}
                    aria-expanded={open}
                    aria-controls={contentId}
                    // `relative` is required BY `touch-hit-44`, not decoration: the 44px hit area is a
                    // transparent `::after` on a coarse pointer, and without a containing block it
                    // resolves against the nearest positioned ancestor and lands somewhere else on the
                    // page. Pinned by tapTargets.contract.test.tsx, which caught this exact omission.
                    //
                    // `border-border` UNDILUTED: `EMPTY-STATES-75`, 2026-09-02 (wave 17 batch H). This
                    // is a control, so WCAG 1.4.11 asks 3:1 of the boundary a member needs in order to
                    // identify it, and the `/60` suffix painted it at roughly half that while the
                    // inert surfaces beside it cleared the floor comfortably. Fixed at the CALL SITE,
                    // never on `--border` — that token is `DASHBOARD-35` and `DISCOVERY-5`'s joint
                    // decision and neither of their numbers may move. Ratcheted by
                    // `src/components/ui/borderTokenContrast.spec.ts`, whose inventory this file left.
                    className="standard-hover relative touch-hit-44 lg:hidden shrink-0 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    data-playwright-id={dataPlaywrightId ? `${dataPlaywrightId}-toggle` : undefined}
                >
                    {open ? localizer("profile.section.collapse") : localizer("profile.section.expand")}
                </button>
            </div>

            {/* The whole breakpoint contract, in one class string: hidden below `lg` unless the member
                opened it, unconditionally shown at `lg` and up. */}
            <div
                id={contentId}
                className={`mt-3 ${open ? "" : "hidden"} lg:block`}
                data-playwright-id={dataPlaywrightId ? `${dataPlaywrightId}-content` : undefined}
            >
                {children}
            </div>
        </section>
    );
}

export default ProfileCollapsibleSection;
